import React, { useState, useEffect, useRef, useContext } from 'react';
import { H } from 'highlight.run';
import './App.css';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Link as MuiLink,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Route, Routes, Navigate, Link, useLocation } from 'react-router-dom';
import NavigationMenu from './components/NavigationMenu';
import SignUp from './components/sign-up/SignUp';
import SignIn from './components/sign-in/SignIn';
import Dashboard from './pages/Dashboard';
import Harvests from './pages/Harvests';
import Orders from './pages/Orders';
import Tasks from './pages/Tasks';
import Management from './pages/Management';
import AccountPage from './components/AccountPage';
import CodeGen from './components/CodeGen';
import Help from './pages/Help';
import DevRulesTest from './pages/DevRulesTest';
import { db } from './firebaseConfig';
import {
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import ProtectedRoute from './components/ProtectedRoute';
import AnalyticsListener from "./AnalyticsListener";
import appLogo from './micgromanlogo.svg';
import HygroLogo from './hygrologo.svg';
import { useAuth } from './auth/AuthProvider';
import RequireAuth from './auth/RequireAuth';
import { AppContext } from './AppContext';
import { SnackbarContext } from './SnackbarContext';
import { normalizeInviteCode, validateInviteCode } from './utils/inviteCode';
import { canAccessDevRulesTest } from './utils/devRulesTestAccess';
import { joinFarmByInviteCodeClient } from './utils/joinFarmByInviteCodeClient';

function App({ darkMode, setDarkMode }) {
  const [isSignUpOpen, setSignUpOpen] = useState(false);
  const [isSignInOpen, setSignInOpen] = useState(false);
  const { user, authReady, signOut } = useAuth();
  const { farmData } = useContext(AppContext);
  const { showSuccess, showError } = useContext(SnackbarContext);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);

  const mainRef = useRef(null);
  const { pathname } = useLocation();
  const isLoggedOutWelcome = pathname === '/' && !user;

  // Fetch Firestore user document
  useEffect(() => {
    if (!user) {
      setUserData(null);
      return;
    }
    const userDocRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
      if (docSnap.exists()) {
        setUserData(docSnap.data());
      }
    });
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    // when the URL path changes…
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;        // reset the scrollContainer
    }
  }, [pathname]);

 useEffect(() => {
   setLoading(!authReady);
 }, [authReady]);

  useEffect(() => {
    if (!user?.uid || !userData?.activeFarmId) return;

    const syncActiveMembershipProfile = async () => {
      try {
        await setDoc(
          doc(db, 'farms', userData.activeFarmId, 'members', user.uid),
          {
            uid: user.uid,
            displayName: userData?.displayName || user.displayName || '',
            photoURL: userData?.photoURL || user.photoURL || '',
          },
          { merge: true }
        );
      } catch (error) {
        console.warn('Failed to sync active member profile:', error);
      }
    };

    syncActiveMembershipProfile();
  }, [
    user?.uid,
    user?.displayName,
    user?.photoURL,
    userData?.activeFarmId,
    userData?.displayName,
    userData?.photoURL,
  ]);

 // 3) Highlight identify: once we have both user & userData
 useEffect(() => {
   if (user && userData) {
     H.identify(user.uid, {
       email:       user.email,
       farmName:    farmData?.farmName || userData.farmName || '',
       displayName: userData.displayName || '',
     });
   }
 }, [user, userData, farmData]);

  const handleOpenSignUp = () => setSignUpOpen(true);
  const handleCloseSignUp = () => setSignUpOpen(false);
  const handleOpenSignIn = () => setSignInOpen(true);
  const handleCloseSignIn = () => setSignInOpen(false);

  const switchToSignIn = () => {
    setSignUpOpen(false);
    setSignInOpen(true);
  };
  const switchToSignUp = () => {
    setSignUpOpen(true);
    setSignInOpen(false);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      setSignInOpen(false);
      setSignUpOpen(false);
      setUserData(null);
    } catch (error) {
      console.error("Logout error:", error.message);
    }
  };

  const handleSelectFarm = async (farmId) => {
    if (!user?.uid || !farmId) return;
    try {
      await updateDoc(doc(db, 'users', user.uid), { activeFarmId: farmId });
    } catch (error) {
      showError('Failed to switch farm.');
    }
  };

  const handleSubmitInviteCode = async (rawCode) => {
    if (!user?.uid) {
      showError('You must be signed in to join a farm.');
      return false;
    }

    const normalized = normalizeInviteCode(rawCode);
    if (!validateInviteCode(normalized)) {
      showError('Code should be 15 characters.');
      return false;
    }

    try {
      await joinFarmByInviteCodeClient(normalized);

      showSuccess('Joined farm successfully.');
      return true;
    } catch (error) {
      const code = String(error?.code || '');
      const message = String(error?.message || '');
      if (message.toLowerCase().includes('no farm found') || code.includes('not-found')) {
        showError('No farm found for that code.');
      } else if (code.includes('unauthenticated')) {
        showError('You are not authenticated. Please sign out and sign back in.');
      } else if (code.includes('invalid-argument')) {
        showError('Invalid invite code.');
      } else {
        showError(`Failed to join farm (${code || 'unknown'}).`);
      }
      return false;
    }
  };

  return (
    <>
      <AnalyticsListener />
      {/* FULL‐VIEWPORT FLEX WRAPPER */}
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: (theme) => theme.palette.background.paper,
          color: (theme) => theme.palette.text.primary
        }}
      >
        {/* NAVIGATION */}
        <NavigationMenu
          darkMode={darkMode}
          setDarkMode={setDarkMode}
          user={user}
          userData={userData}
          farmData={farmData}
          loading={loading}
          onOpenSignIn={handleOpenSignIn}
          onOpenSignUp={handleOpenSignUp}
          onLogout={handleLogout}
          onSubmitInviteCode={handleSubmitInviteCode}
          onSelectFarm={handleSelectFarm}
        />

        {/* MAIN CONTENT – scrolls internally */}
        <Box
          component="main"
          ref={mainRef}
          sx={{
            flexGrow: 1,
            width: '100%',
            overflowY: isLoggedOutWelcome ? 'hidden' : 'auto',
            overflowX: 'hidden',
            // padding bottom to accommodate any fixed footer
            pb: isLoggedOutWelcome ? 0 : '150px',
            pt: isLoggedOutWelcome
              ? 0
              : {
                  xs: '70px',
                  sm: '70px',
                  md: '40px',
                },
          }}
        >
          <Routes>
            <Route
              path="/"
              element={
                !authReady ? (
                  <Box sx={{ minHeight: "60vh", display: "grid", placeItems: "center" }}>
                    <CircularProgress />
                  </Box>
                ) : user ? (
                  <Navigate to="/dashboard" replace />
                ) : (
	                  <Box
	                    sx={{
	                      backgroundColor: (theme) => theme.palette.background.default,
	                      height: {
	                        xs: 'calc(100dvh - 56px - 88px)',
	                        sm: 'calc(100dvh - 56px - 88px)',
	                        md: 'calc(100dvh - 64px - 88px)',
	                      },
	                      boxSizing: 'border-box',
	                      color: (theme) => theme.palette.text.primary,
	                      display: 'flex',
	                      flexDirection: 'column',
	                      p: 2,
	                      alignItems: 'center',
	                      justifyContent: 'center',
	                    }}
	                  >
                      <Typography variant="h3" sx={{ color: '#ff7700', mb: 2 }}>
	                      Welcome to...
	                    </Typography>
	                    <Box
	                      component="img"
	                      src={appLogo}
	                      alt="MicGro Manager logo"
	                      sx={{
                          width: { xs: 300, sm: 360 },
                          height: 'auto',
                          mb: 3,
                          filter: darkMode ? 'drop-shadow(0 6px 14px rgba(0, 0, 0, 0.35))' : 'none',
                        }}
	                    />
                    <Typography variant="h5" sx={{ mb: 2 }}>
                      Please login or signup to continue.
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#266cff', mt: 1 }}>
                      <Link to="/help" style={{ textDecoration: 'none' }}>
                        Need Help?
                      </Link>
                    </Typography>
                    <Box
                      component="footer"
                      sx={{
                        position: 'fixed',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        textAlign: 'center',
                        py: 1.5,
                        px: 2,
                        backgroundColor: (theme) => theme.palette.background.paper,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        zIndex: (theme) => theme.zIndex.appBar - 1,
                      }}
                    >
                      <MuiLink
                        href="https://hy-gro.co.uk"
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                      >
                        <img src={HygroLogo} alt="Hy-Gro Hydroponics" style={{ width: 170, height: 'auto' }} />
                      </MuiLink>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', mt: 0.5 }}>
                        © {new Date().getFullYear()} Hy-Gro Hydroponics LTD
                      </Typography>
                    </Box>
                  </Box>
                )
              }
            />

            <Route
              path="/dashboard"
              element={
                <RequireAuth>
                  <ProtectedRoute user={user} userData={userData}>
                    <Dashboard userData={userData} />
                  </ProtectedRoute>
                </RequireAuth>
              }
            />

            <Route
              path="/harvests"
              element={
                <RequireAuth>
                  <ProtectedRoute user={user} userData={userData}>
                    <Harvests userData={userData} />
                  </ProtectedRoute>
                </RequireAuth>
              }
            />

            <Route
              path="/orders"
              element={
                <RequireAuth>
                  <ProtectedRoute user={user} userData={userData}>
                    <Orders userData={userData} />
                  </ProtectedRoute>
                </RequireAuth>
              }
            />

            <Route
              path="/tasks"
              element={
                <RequireAuth>
                  <ProtectedRoute user={user} userData={userData}>
                    <Tasks userData={userData} />
                  </ProtectedRoute>
                </RequireAuth>
              }
            />

            <Route
              path="/management"
              element={
                <RequireAuth>
                  <ProtectedRoute user={user} userData={userData}>
                    <Management userData={userData} />
                  </ProtectedRoute>
                </RequireAuth>
              }
            />

            <Route
              path="/account"
              element={
                <RequireAuth>
                  <ProtectedRoute
                    user={user}
                    userData={userData}
                    allowUnapproved={true}
                  >
                    <AccountPage userData={userData} />
                  </ProtectedRoute>
                </RequireAuth>
              }
            />

            <Route
              path="/codegen"
              element={
                <RequireAuth>
                  <ProtectedRoute user={user} userData={userData} devOnly={true}>
                    <CodeGen />
                  </ProtectedRoute>
                </RequireAuth>
              }
            />

            <Route path="/help" element={<Help />} />
            <Route
              path="/dev-rules-test"
              element={
                <RequireAuth>
                  <ProtectedRoute user={user} userData={userData} allowUnapproved={true}>
                    {canAccessDevRulesTest(user, userData) ? (
                      <DevRulesTest user={user} userData={userData} />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )}
                  </ProtectedRoute>
                </RequireAuth>
              }
            />
          </Routes>
        </Box>

        {/* AUTH DIALOGS (overlays, so placement here is fine) */}
        {!user && (
          <>
            <Dialog
              open={isSignUpOpen}
              onClose={handleCloseSignUp}
              fullWidth
              maxWidth="sm"
            >
              <DialogContent sx={{ p: 0 }}>
                <IconButton
                  aria-label="close"
                  onClick={handleCloseSignUp}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <CloseIcon />
                </IconButton>
                <SignUp switchToSignIn={switchToSignIn} sx={{ boxShadow: 'none', bgcolor: 'transparent' }} />
              </DialogContent>
            </Dialog>

            <Dialog
              open={isSignInOpen}
              onClose={handleCloseSignIn}
              fullWidth
              maxWidth="sm"
            >
              <DialogContent sx={{ p: 0 }}>
                <IconButton
                  aria-label="close"
                  onClick={handleCloseSignIn}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <CloseIcon />
                </IconButton>
                <SignIn switchToSignUp={switchToSignUp} sx={{ boxShadow: 'none', bgcolor: 'transparent' }} />
              </DialogContent>
            </Dialog>
          </>
        )}
      </Box>
    </>
  );
}

export default App;
